(() => {
  const normalise = (value) => value.trim().toLocaleLowerCase();
  const cardStates = new WeakMap();

  const getCardState = (swatch) => {
    const card = swatch.closest('[data-card-variant-card]');

    if (!card) {
      return null;
    }

    let state = cardStates.get(card);

    if (state) {
      return state;
    }

    const image = card.querySelector('[data-card-main-image]');

    state = {
      card,
      image,
      original: image
        ? {
            src: image.getAttribute('src'),
            srcset: image.getAttribute('srcset'),
            alt: image.getAttribute('alt'),
          }
        : null,
      selectedImage: null,
      selectedAlt: '',
      selectedSwatch: card.querySelector('[data-card-variant-swatch].is-selected'),
    };

    state.selectedImage = state.original?.src || null;
    state.selectedAlt = state.original?.alt || '';
    cardStates.set(card, state);

    return state;
  };

  const setImage = (state, imageUrl, alt, isPreview) => {
    if (!state?.image) {
      return;
    }

    if (imageUrl) {
      state.image.src = imageUrl;
      state.image.srcset = imageUrl;
      if (alt) {
        state.image.alt = alt;
      }
    } else if (state.original) {
      state.image.src = state.original.src || '';
      if (state.original.srcset) {
        state.image.srcset = state.original.srcset;
      } else {
        state.image.removeAttribute('srcset');
      }
      state.image.alt = state.original.alt || '';
    }

    state.card.toggleAttribute('data-card-variant-preview', isPreview);
  };

  const previewVariant = (swatch) => {
    const state = getCardState(swatch);

    if (!state) {
      return;
    }

    setImage(state, swatch.dataset.variantImage || state.selectedImage, swatch.dataset.variantImageAlt || state.selectedAlt, true);
  };

  const restoreSelectedVariant = (swatch) => {
    const state = getCardState(swatch);

    if (!state) {
      return;
    }

    setImage(state, state.selectedImage, state.selectedAlt, false);
  };

  const selectVariant = (swatch) => {
    const state = getCardState(swatch);

    if (!state) {
      return;
    }

    const cardSwatches = state.card.querySelectorAll('[data-card-variant-swatch]');
    cardSwatches.forEach((cardSwatch) => {
      const isSelected = cardSwatch === swatch;
      cardSwatch.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        cardSwatch.setAttribute('aria-current', 'true');
      } else {
        cardSwatch.removeAttribute('aria-current');
      }
    });

    const labelValue = state.card.querySelector('.card-variant-swatches__label-value');
    if (labelValue) {
      labelValue.textContent = swatch.title || '';
    }

    state.selectedSwatch = swatch;
    state.selectedImage = swatch.dataset.variantImage || state.original?.src || null;
    state.selectedAlt = swatch.dataset.variantImageAlt || swatch.title || state.original?.alt || '';
    setImage(state, state.selectedImage, state.selectedAlt, true);
  };

  const isFinePointer = (event) => {
    if (event.pointerType === 'touch') {
      return false;
    }

    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  };

  const getSwatchFromEvent = (event) => {
    if (!event.target || typeof event.target.closest !== 'function') {
      return null;
    }

    return event.target.closest('[data-card-variant-swatch]');
  };

  const initialiseVariantInteractions = () => {
    if (document.documentElement.hasAttribute('data-noir-variant-swatches-ready')) {
      return;
    }

    document.documentElement.setAttribute('data-noir-variant-swatches-ready', '');

    document.addEventListener(
      'click',
      (event) => {
        const swatch = getSwatchFromEvent(event);
        if (!swatch || !swatch.dataset.variantId) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        selectVariant(swatch);
      },
      true
    );

    document.addEventListener('pointerover', (event) => {
      const swatch = getSwatchFromEvent(event);
      if (!swatch || !isFinePointer(event)) {
        return;
      }

      if (event.relatedTarget && swatch.contains(event.relatedTarget)) {
        return;
      }

      previewVariant(swatch);
    });

    document.addEventListener('pointerout', (event) => {
      const swatch = getSwatchFromEvent(event);

      if (!swatch || !isFinePointer(event)) {
        return;
      }

      const swatches = swatch.closest('[data-card-variant-swatches]');
      if (event.relatedTarget && swatches?.contains(event.relatedTarget)) {
        return;
      }

      restoreSelectedVariant(swatch);
    });

    document.addEventListener('focusin', (event) => {
      const swatch = getSwatchFromEvent(event);

      if (swatch) {
        previewVariant(swatch);
      }
    });

    document.addEventListener('focusout', (event) => {
      const swatch = getSwatchFromEvent(event);

      if (!swatch) {
        return;
      }

      const swatches = swatch.closest('[data-card-variant-swatches]');
      if (event.relatedTarget && swatches?.contains(event.relatedTarget)) {
        return;
      }

      restoreSelectedVariant(swatch);
    });
  };

  initialiseVariantInteractions();

  const getRoots = (root = document) => {
    if (root.matches && root.matches('[data-collection-root]')) {
      return [root];
    }

    return [...root.querySelectorAll('[data-collection-root]')];
  };

  const setLoadingState = (button, isLoading) => {
    if (!button || !button.isConnected) {
      return;
    }

    button.dataset.loading = isLoading ? 'true' : 'false';
    button.toggleAttribute('aria-busy', isLoading);
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    button.classList.toggle('is-loading', isLoading);
  };

  const applySearch = (state) => {
    const grid = state.root.querySelector('#product-grid');
    const items = grid ? [...grid.querySelectorAll(':scope > .grid__item')] : [];
    const query = normalise(state.input.value);
    let visibleCount = 0;

    items.forEach((item) => {
      const title = normalise(item.dataset.collectionProductTitle || item.querySelector('.card__heading')?.textContent || '');
      const isMatch = query === '' || title.includes(query);

      item.hidden = !isMatch;

      if (isMatch) {
        visibleCount += 1;
      }
    });

    state.clear.hidden = query === '';
    state.status.hidden = query === '' || items.length === 0;

    if (query !== '' && items.length > 0) {
      state.status.textContent = visibleCount > 0
        ? (visibleCount === 1 ? state.input.dataset.resultsOne : state.input.dataset.resultsOther).replace('[count]', visibleCount)
        : state.input.dataset.noResults;
    }
  };

  const disconnectLoadMoreObserver = (state) => {
    if (state.loadMoreObserver) {
      state.loadMoreObserver.disconnect();
      state.loadMoreObserver = null;
    }

    state.loadMoreButton = null;
  };

  const syncLoadMoreObserver = (state) => {
    if (typeof IntersectionObserver === 'undefined') {
      disconnectLoadMoreObserver(state);
      return;
    }

    const button = state.root.querySelector('[data-collection-load-more]');

    if (!button) {
      disconnectLoadMoreObserver(state);
      return;
    }

    if (state.loadMoreButton === button && state.loadMoreObserver) {
      return;
    }

    disconnectLoadMoreObserver(state);
    state.loadMoreButton = button;
    state.loadMoreArmed = true;
    state.loadMoreObserver = new IntersectionObserver(
      ([entry]) => {
        if (!button.isConnected || state.loadMoreButton !== button) {
          return;
        }

        if (!entry.isIntersecting) {
          state.loadMoreArmed = true;
          return;
        }

        if (!state.loadMoreArmed || button.dataset.loading === 'true') {
          return;
        }

        state.loadMoreArmed = false;
        loadMore(state, button);
      },
      {
        rootMargin: '0px 0px 600px 0px',
      }
    );
    state.loadMoreObserver.observe(button);
  };

  const loadMore = async (state, button) => {
    if (button.dataset.loading === 'true') {
      return;
    }

    const currentGrid = state.root.querySelector('#product-grid');

    if (!currentGrid) {
      return;
    }

    state.loadMoreArmed = false;
    setLoadingState(button, true);
    button.textContent = button.dataset.loadingLabel;

    try {
      const response = await fetch(button.href, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Collection page request failed with status ${response.status}`);
      }

      const responseText = await response.text();
      const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const incomingGrid = parsedDocument.querySelector('#product-grid');
      const incomingItems = incomingGrid ? incomingGrid.querySelectorAll(':scope > .grid__item') : [];

      if (!incomingGrid || incomingItems.length === 0) {
        throw new Error('Collection page response did not include product cards');
      }

      incomingItems.forEach((item) => currentGrid.append(item));

      const incomingButton = parsedDocument.querySelector('[data-collection-load-more]');

      if (incomingButton) {
        button.href = incomingButton.href;
        button.dataset.defaultLabel = incomingButton.dataset.defaultLabel || button.dataset.defaultLabel;
        button.dataset.loadingLabel = incomingButton.dataset.loadingLabel || button.dataset.loadingLabel;
        button.dataset.errorLabel = incomingButton.dataset.errorLabel || button.dataset.errorLabel;
        button.textContent = button.dataset.defaultLabel;
        setLoadingState(button, false);
      } else {
        button.remove();
      }

      applySearch(state);
      syncLoadMoreObserver(state);
    } catch (error) {
      console.error(error);
      button.textContent = button.dataset.errorLabel || button.dataset.defaultLabel;
      setLoadingState(button, false);
      syncLoadMoreObserver(state);
    }
  };

  const initialiseRoot = (root) => {
    if (root.dataset.collectionToolsReady === 'true') {
      return;
    }

    const input = root.querySelector('[data-collection-product-search]');
    const clear = root.querySelector('[data-collection-product-search-clear]');
    const status = root.querySelector('[data-collection-product-search-status]');
    const backToTop = root.querySelector('[data-collection-back-to-top]');
    const gridContainer = root.querySelector('#ProductGridContainer');

    if (!input || !clear || !status || !backToTop) {
      return;
    }

    const state = {
      root,
      input,
      clear,
      status,
      loadMoreObserver: null,
      loadMoreButton: null,
      loadMoreArmed: true,
    };

    input.addEventListener('input', () => applySearch(state));

    clear.addEventListener('click', () => {
      input.value = '';
      applySearch(state);
      input.focus();
    });

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-collection-load-more]');

      if (!button || !root.contains(button)) {
        return;
      }

      event.preventDefault();
      state.loadMoreArmed = false;
      loadMore(state, button);
    });

    if (gridContainer) {
      const observer = new MutationObserver(() => {
        window.requestAnimationFrame(() => {
          applySearch(state);
          syncLoadMoreObserver(state);
        });
      });

      observer.observe(gridContainer, { childList: true, subtree: true });
    }

    const updateBackToTop = () => {
      backToTop.hidden = window.scrollY < 480;
    };

    backToTop.addEventListener('click', (event) => {
      event.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });

    window.addEventListener('scroll', updateBackToTop, { passive: true });
    updateBackToTop();
    applySearch(state);
    syncLoadMoreObserver(state);
    root.dataset.collectionToolsReady = 'true';
  };

  const initialise = (root = document) => {
    getRoots(root).forEach(initialiseRoot);
  };

  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
